import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Download } from 'lucide-react';
import { useRuntimeUpdates } from '../hooks/useRuntimeUpdates';

export default function RuntimeUpdateBanner() {
    const { active, error, requestingId, requestUpdate } = useRuntimeUpdates();
    const [queued, setQueued] = useState(false);
    if (!active) return null;

    const compatible = active.compatibility === 'clean';
    const requested = active.requested || active.state === 'requested';
    const target = active.targetRuntimeVersion || 'Unknown version';
    const compatibilityDetails = active.findings?.map((finding) => finding.explanation || finding.message || 'Compatibility finding').join(' ');
    const onRequest = async () => {
        try {
            await requestUpdate(active.id);
            setQueued(true);
        } catch {
            // Hook exposes the server error below without throwing into the shell.
        }
    };

    return (
        <aside
            className={`mx-0 mt-0 w-full rounded-none border-x-0 border-t-0 px-4 py-2 shadow-sm ${compatible ? 'border-sky-200 bg-sky-50' : 'border-amber-200 bg-amber-50'}`}
            role="status"
            data-testid="runtime-update-banner"
        >
            <div className="flex min-h-8 items-center justify-center gap-3 whitespace-nowrap">
                {compatible ? <Download className="h-4 w-4 flex-shrink-0 text-sky-700" /> : <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-700" />}
                <div className="flex min-w-0 items-center gap-2 overflow-x-auto text-center">
                    <p className="text-sm font-semibold text-ink-1">Runtime update available</p>
                    <p className="text-xs text-ink-2">{target}</p>
                    {!compatible && (
                        <span className="max-w-[36rem] truncate text-xs text-amber-800" title={compatibilityDetails || 'This update may strand custom modules.'}>
                            Compatibility warning
                        </span>
                    )}
                    {error && <p className="text-xs text-rose-700" role="alert">{error}</p>}
                    {(requested || queued) && (
                        <p className="flex items-center gap-1 text-xs font-medium text-emerald-700" title="Completion will be confirmed by runtime health polling.">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Update queued
                        </p>
                    )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                    {compatible && !requested && !queued && (
                        <button
                            type="button"
                            onClick={onRequest}
                            disabled={requestingId === active.id}
                            className="rounded-md bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-800 disabled:opacity-50"
                        >
                            {requestingId === active.id ? 'Queueing…' : 'Update now'}
                        </button>
                    )}
                </div>
            </div>
        </aside>
    );
}
